import { ReactElement, useCallback, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Fade, Popper, useMediaQuery } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';

import { GTMAppInspectorTriggers } from '@localstack/constants';
import { pushGTMEvent, useKeyPress } from '@localstack/services';

import { useHeaderOffsetModifiers, useHoverIntent, useNudgeVisibility } from './hooks';
import {
  Anchor,
  Bullet,
  BulletIcon,
  BulletList,
  Card,
  CardHeader,
  CardTitle,
  Description,
  DismissButton,
  Dot,
  HeaderContent,
  MOTION_DURATION,
  MOTION_EASING,
  MotionFrame,
  NUDGE_BULLETS,
  NUDGE_ID,
  ProductBadge,
  REDUCED_MOTION_QUERY,
} from './styles';

export type AppInspectorNudgeProps = {
  children: ReactElement;
};

export const AppInspectorNudge = ({ children }: AppInspectorNudgeProps): ReactElement => {
  const posthog = usePostHog();
  const hasFiredTabClickRef = useRef(false);
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  const trackNudgeEvent = useCallback((trigger: GTMAppInspectorTriggers) => pushGTMEvent(trigger, posthog), [posthog]);
  const handleAutoShow = useCallback(() => trackNudgeEvent(GTMAppInspectorTriggers.NUDGE_SHOWN), [trackNudgeEvent]);
  const { handleBlur, handleFocus, handleHoverEnd, handleHoverStart, isHovered, isKeyboardFocused, resetHover } =
    useHoverIntent<HTMLSpanElement>();
  const { autoShowIntro, dismissIntro, hasVisited, isIntroOpen, markVisited, shouldAnimateAutoShow, showDot } =
    useNudgeVisibility({ anchorEl, isHovered, onAutoShow: handleAutoShow });
  const popperModifiers = useHeaderOffsetModifiers(anchorEl);

  // Only track the click that converts the user, not later visits.
  const handleTabClick = useCallback(() => {
    if (!hasVisited && !hasFiredTabClickRef.current) {
      hasFiredTabClickRef.current = true;
      trackNudgeEvent(GTMAppInspectorTriggers.TAB_CLICKED);
    }
    markVisited();
  }, [hasVisited, markVisited, trackNudgeEvent]);

  const handleDismiss = useCallback(() => {
    trackNudgeEvent(GTMAppInspectorTriggers.NUDGE_DISMISSED);
    dismissIntro();
    resetHover();
  }, [dismissIntro, resetHover, trackNudgeEvent]);

  const onEscape = useCallback(() => {
    if (autoShowIntro) handleDismiss();
  }, [autoShowIntro, handleDismiss]);

  useKeyPress(onEscape, 'Escape');

  return (
    <Anchor
      ref={setAnchorEl}
      onClick={handleTabClick}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-describedby={isIntroOpen ? NUDGE_ID : undefined}
    >
      {children}
      {showDot && <Dot aria-hidden />}
      <Popper
        id={NUDGE_ID}
        open={isIntroOpen}
        anchorEl={anchorEl}
        placement="bottom"
        transition
        modifiers={popperModifiers}
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        {({ placement, TransitionProps }) => {
          // Keyboard focus and reduced motion stay instant.
          const motionDisabled =
            prefersReducedMotion || (!autoShowIntro && isKeyboardFocused) || (autoShowIntro && !shouldAnimateAutoShow);
          const motionState = motionDisabled ? 'none' : TransitionProps?.in ? 'enter' : 'exit';

          return (
            <Fade {...TransitionProps} timeout={motionDisabled ? 0 : MOTION_DURATION} easing={MOTION_EASING}>
              <MotionFrame data-motion={motionState} data-placement={placement}>
                <Card
                  elevation={0}
                  role={autoShowIntro ? 'dialog' : 'tooltip'}
                  aria-label="App Inspector feature introduction"
                  onMouseEnter={handleHoverStart}
                  onMouseLeave={handleHoverEnd}
                  // Popper content is portal-rendered, but React events still bubble to the anchor.
                  onClick={(event) => event.stopPropagation()}
                >
                  <CardHeader>
                    <HeaderContent>
                      <ProductBadge label="NEW" size="small" />
                      <CardTitle variant="subtitle1">Find bugs before deployment</CardTitle>
                    </HeaderContent>
                    {autoShowIntro && (
                      <DismissButton aria-label="Dismiss App Inspector tip" size="small" onClick={handleDismiss}>
                        <CloseRounded fontSize="small" />
                      </DismissButton>
                    )}
                  </CardHeader>
                  <Description variant="body2">
                    App Inspector gives you a complete view of how your application behaves before deployment, including
                    data, events, and service interactions—without cloud redeploys or scattered logs.
                  </Description>
                  <BulletList>
                    {NUDGE_BULLETS.map(({ Icon, text }) => (
                      <Bullet key={text} component="li" variant="body2">
                        <BulletIcon>
                          <Icon aria-hidden />
                        </BulletIcon>
                        <span>{text}</span>
                      </Bullet>
                    ))}
                  </BulletList>
                </Card>
              </MotionFrame>
            </Fade>
          );
        }}
      </Popper>
    </Anchor>
  );
};
